// src/couponApi.js
import axios from 'axios';
import config from './config';

const API_BASE_URL = `${config.API_BASE_URL}/Coupons`;

// ==================== التحقق من الكوبون (عند الدفع) ====================

/**
 * التحقق من صلاحية رمز الكوبون قبل تطبيقه على الطلب
 * @param {Object} data - { code, branchId, userId, subTotal, currency }
 * @returns {Object} - نتيجة التحقق مع معلومات الخصم إذا كان صالحاً
 */
export const validateCoupon = async (data) => {
  if (!data || !data.code) {
    return { valid: false, message: 'رمز الكوبون مطلوب.' };
  }

  try {
    const response = await axios.post(`${API_BASE_URL}/validate`, {
      code: data.code.trim().toUpperCase(),
      branchId: data.branchId || null,
      userId: data.userId || null,
      subTotal: data.subTotal || 0,
      currency: data.currency || 'YER'
    });

    return response.data;
  } catch (error) {
    // الخادم يرجع 400 مع تفاصيل خطأ التحقق
    if (error.response && error.response.data) {
      return error.response.data;
    }
    console.error('خطأ في التحقق من الكوبون:', error);
    return { valid: false, message: 'فشل التحقق من الكوبون. يرجى المحاولة مرة أخرى.' };
  }
};

// ==================== جلب الكوبونات المتاحة للفرع ====================

/**
 * جلب جميع الكوبونات النشطة المتاحة لفرع معين
 * يشمل الكوبونات العامة + الكوبونات الخاصة بالفرع
 * @param {number} branchId - رقم الفرع
 * @returns {Array} - قائمة الكوبونات المتاحة
 */
export const fetchCouponsForBranch = async (branchId) => {
  if (!branchId) {
    console.log('جلب كوبونات الفرع: لا يوجد رقم فرع، تم التخطي.');
    return [];
  }

  try {
    const response = await axios.get(`${API_BASE_URL}/branch/${branchId}`);
    const coupons = response.data || [];

    return coupons.map(item => ({
      id: item.Id || item.id,
      code: item.Code || item.code,
      type: item.Type || item.type,
      discountType: item.DiscountType || item.discountType,
      value: parseFloat(item.Value || item.value || 0),
      currency: item.Currency || item.currency || 'YER',
      currencySymbol: item.CurrencySymbol || item.currencySymbol || '',
      formattedValue: item.FormattedValue || item.formattedValue || '',
      minOrderAmount: parseFloat(item.MinOrderAmount || item.minOrderAmount || 0),
      formattedMinOrderAmount: item.FormattedMinOrderAmount || item.formattedMinOrderAmount || '',
      maxDiscountAmount: item.MaxDiscountAmount || item.maxDiscountAmount || null,
      formattedMaxDiscountAmount: item.FormattedMaxDiscountAmount || item.formattedMaxDiscountAmount || null,
      usageLimit: item.UsageLimit || item.usageLimit || null,
      usageLimitPerUser: item.UsageLimitPerUser || item.usageLimitPerUser || 1,
      usedCount: item.UsedCount || item.usedCount || 0,
      remainingUses: item.RemainingUses || item.remainingUses || null,
      startDate: item.StartDate || item.startDate || null,
      endDate: item.EndDate || item.endDate || null,
      isActive: item.IsActive ?? item.isActive ?? true,
      isGlobal: item.IsGlobal ?? item.isGlobal ?? true,
      description: item.Description || item.description || '',
      isForAllUsers: item.IsForAllUsers ?? item.isForAllUsers ?? true
    }));
  } catch (error) {
    console.error('خطأ في جلب كوبونات الفرع:', error);
    return [];
  }
};

// ==================== التحقق من أهلية المستخدم للكوبون ====================

/**
 * فحص سريع: التحقق من الكوبون بمعلومات بسيطة (الرمز + رقم المستخدم)
 * مفيد لعرض إشعارات "لديك كوبون"
 * @param {string} code - رمز الكوبون
 * @param {number} userId - رقم المستخدم
 * @param {number} branchId - رقم الفرع
 * @returns {Object} - { valid, message, ... }
 */
export const checkCouponEligibility = async (code, userId, branchId) => {
  if (!code || !userId) {
    return { valid: false, message: 'رمز الكوبون ورقم المستخدم مطلوبان.' };
  }

  try {
    const response = await axios.post(`${API_BASE_URL}/validate`, {
      code: code.trim().toUpperCase(),
      userId: userId,
      branchId: branchId || null,
      subTotal: 0,
      currency: 'YER'
    });

    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      return error.response.data;
    }
    return { valid: false, message: 'تعذر التحقق من الكوبون.' };
  }
};